import { Bell } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `notifications-module/design.md` §11-§13: sin `requiredPermission`
 * (`null`) porque la lectura y gestión propias son `auth-only` — cualquier
 * usuario autenticado ve su propia campana, no hace falta un permiso del
 * catálogo RBAC. Labels citados textualmente de
 * `apps/web-admin/src/routes/notifications/notification-list-page.tsx`
 * ("Mis notificaciones", "Fallidas", "Marcar leída", "Reintentar") y
 * `apps/web-admin/src/routes/account/notification-preferences-page.tsx`
 * ("Avisos no críticos por email", "Avisos no críticos en la campana").
 */
export const NOTIFICATIONS_MODULE: GuideModule = {
  id: 'notifications',
  title: 'Notificaciones',
  icon: Bell,
  requiredPermission: null,
  priority: 9,
  summary:
    'La campana de notificaciones te avisa de eventos que te interesan: confirmaciones de pedido, cierres de corte, solicitudes de cambio, recordatorios de corte próximo y más. Cada usuario ve solo sus propias notificaciones. Las confirmaciones directas de tus pedidos siempre llegan; los avisos operativos no críticos (como recordatorios) respetan tus preferencias y podés desactivarlos por canal, por email o dentro de la campana, sin perder las confirmaciones importantes. Si tenés el permiso de gestión de notificaciones, además podés ver qué envíos fallaron y reintentarlos manualmente.',
  steps: [
    {
      title: 'Ver tus notificaciones sin leer',
      description:
        'La campana en la barra superior muestra un número cuando tenés notificaciones sin leer. Hacé clic para abrir el panel con las más recientes.',
      keyTerms: ['notificacion-in-app', 'no-leida'],
    },
    {
      title: 'Marcar una notificación como leída',
      description:
        'Desde el panel de la campana o desde "Notificaciones" → "Mis notificaciones", hacé clic en "Marcar leída" sobre cualquier notificación sin leer.',
      keyTerms: ['notificacion-in-app'],
    },
    {
      title: 'Ver el historial completo',
      description:
        'Hacé clic en "Ver todas" desde el panel de la campana, o andá directamente a "Notificaciones", para ver el listado paginado completo.',
      keyTerms: ['notificacion-in-app'],
    },
    {
      title: 'Ajustar tus preferencias',
      description:
        'Abrí el menú de tu usuario (arriba a la derecha), entrá a "Notificaciones" y desactivá "Avisos no críticos por email" o "Avisos no críticos en la campana" si no querés recibir avisos operativos no críticos por ese canal. Las confirmaciones directas de tus pedidos no se ven afectadas.',
      keyTerms: ['preferencia-de-notificacion', 'aviso-no-critico'],
    },
    {
      title: 'Reintentar un envío fallido (con permiso de gestión)',
      description:
        'En "Notificaciones", cambiá a la pestaña "Fallidas" (solo la ven quienes tienen el permiso para administrar notificaciones) y hacé clic en "Reintentar" sobre cualquier fila para reencolarla y que el sistema la vuelva a intentar.',
      keyTerms: ['entrega-fallida'],
    },
  ],
  states: [
    {
      value: 'SENT',
      label: 'Enviada',
      description: 'La notificación se entregó correctamente por ese canal.',
    },
    {
      value: 'FAILED',
      label: 'Fallida',
      description: 'La entrega falló después de agotar los reintentos automáticos.',
    },
    {
      value: 'SKIPPED',
      label: 'Omitida',
      description: 'El destinatario desactivó ese canal para avisos no críticos.',
    },
  ],
  faq: [
    {
      question: '¿Por qué no me llegó un aviso de recordatorio?',
      answer:
        'Puede ser porque desactivaste "Avisos no críticos por email" o "en la campana" en tus preferencias, o porque ya tenías un pedido cargado para esa fecha.',
    },
    {
      question: '¿Puedo desactivar la confirmación de mi propio pedido?',
      answer:
        'No. Las confirmaciones directas de tus pedidos son transaccionales y siempre se envían, independientemente de tus preferencias.',
    },
    {
      question: '¿Qué diferencia hay entre "Fallida" y no recibir nada?',
      answer:
        'Una notificación "Fallida" quedó registrada con el error que la detuvo, visible en la pestaña "Fallidas". Si nunca se generó el evento, no hay ningún registro que ver.',
    },
    {
      question: '¿Quién puede ver y reintentar las notificaciones fallidas?',
      answer:
        'Solo usuarios con el permiso de gestión de notificaciones, normalmente administradores.',
    },
  ],
  tips: [
    'Revisá la campana al empezar tu jornada: agrupa los avisos operativos más recientes.',
    'Si dejás de recibir recordatorios y no era tu intención, revisá tus preferencias antes de suponer un error del sistema.',
    'La pestaña "Fallidas" es la primera parada cuando un cliente dice que "nunca le llegó" un correo.',
    'El link de baja al final de un email no crítico hace lo mismo que desactivar el canal desde tus preferencias.',
  ],
};
